<script setup>
import { computed } from 'vue';

import literacyGraph03 from '@/assets/images/result/literacy_graph_03.png';
import numeracyGraph03 from '@/assets/images/result/numeracy_graph_03.png';

const props = defineProps({ 
  pagetype: { 
    type: String,
    required: true,
    validator: (value) => ['literacy', 'numeracy'].includes(value)
  },
  tableData: { 
    type: Array,
    required: true,
    default: () => []
  },
  // 그래프 관련 prop
  statistics: {
    type: Array,
    default: () => []
  },
  chartHeight: { // CSS 차트 높이
    type: String,
    default: '300px', 
  },
  maxScale: { // 그래프의 최대 스케일
    type: Number,
    default: 80,
  }
});

const mainTitle = computed(() =>
  props.pagetype === 'literacy' ? '하위 영역별 기초 문해력 도달도*' : '하위 영역별 기초 수리력 도달도*'
);
const subDescriptionTitle = computed(() =>
  props.pagetype === 'literacy' ? '*기초 문해력 도달도' : '*기초 수리력 도달도'
);
const descriptionHtml = computed(() =>
  props.pagetype === 'literacy'
    ? `문해력의 각 하위 영역에서 기초 수준에 도달했는지를 나타냅니다. <br><span class="font-medium">1수준과 2수준을 구분하는 컷</span> 점수 지점의 능력을 가진 학생이 해당 영역의 각 점수를 획득할 확률을 구하여, 아래로부터 누적 확률이 16%에 대응하는 원점수보다 낮으면 '주의', 84%에 대응하는 원점수보다 높으면 '안전', 그 사이의 원점수에 해당하면 '경계'입니다. 해석에 있어 '주의'는 해당 하위 영역에서 기초 수준에 도달하지 못한 단계, '경계'는 기초 수준의 경계에 있는 단계, '안전'은 기초 수준 이상에 있는 단계를 의미합니다.`
    : `수리력의 각 하위 영역에서 기초 수준에 도달했는지를 나타냅니다. <br><span class="font-medium">1수준과 2수준을 구분하는 컷</span> 점수 지점의 능력을 가진 학생이 해당 영역의 각 점수를 획득할 확률을 구하여, 아래로부터 누적 확률이 16%에 대응하는 원점수보다 낮으면 '주의', 84%에 대응하는 원점수보다 높으면 '안전', 그 사이의 원점수에 해당하면 '경계'입니다. 해석에 있어 '주의'는 해당 하위 영역에서 기초 수준에 도달하지 못한 단계, '경계'는 기초 수준의 경계에 있는 단계, '안전'은 기초 수준 이상에 있는 단계를 의미합니다.`
);
const graphSrc = computed(() => {
  return props.pagetype === 'literacy' ? literacyGraph03 : numeracyGraph03;
});

// 학생 이름 필터링 함수
const filterStudentName = (name) => {
  if (!name || typeof name !== 'string') {
    return '';
  }
  
  // 쉼표로 구분하여 배열로 변환
  const names = name.split(',')
    .map(n => n.trim()) // 앞뒤 공백 제거
    .filter(n => n.length > 0); // 빈 문자열 제거
  
  // 필터링된 이름들을 다시 쉼표로 연결
  return names.join(', ');
};


const statusHeaderText = computed(() => {
  return props.pagetype === 'literacy' ? '기초 문해력 도달도' : '기초 수리력 도달도';
});

const achievementLevels = computed(() => {
  if (props.pagetype === 'literacy') {
    return [
      { subcate: '어휘', status: ['주의', '경계'] },
      { subcate: '탐색 및 확인', status: ['주의', '경계'] },
      { subcate: '통합 및 해석', status: ['주의', '경계'] },
      { subcate: '비판 및 평가', status: ['주의', '경계'] }
    ];
  } else {
    return [
      { subcate: '수와 양', status: ['주의', '경계'] },
      { subcate: '규칙과 관계', status: ['주의', '경계'] },
      { subcate: '모양과 측정', status: ['주의', '경계'] },
      { subcate: '자료와 불확실성', status: ['주의', '경계'] }
    ];
  }
});

// 명단 테이블에 표시될 행 데이터
const tableRows = computed(() => {
  if (!props.tableData || !Array.isArray(props.tableData) || props.tableData.length === 0) {
    console.warn("Attainment: tableData prop is not a valid array or is empty.");
    return [];
  }
  return achievementLevels.value.map((levelInfo, rowIndex) => {    
    const studentsFromProp = props.tableData[rowIndex]?.students;       
    const students = (studentsFromProp && Array.isArray(studentsFromProp) && studentsFromProp.some(name => name && name.trim() !== '')) ? studentsFromProp : ['']; 
    return {
      subcate: levelInfo.subcate, 
      status: levelInfo.status,
      students: students
    };
  });
});

// === 막대 그래프 관련 ===
const fixedGroups = computed(() => {
    return [
        { key: 'attention', label: '주의' }, 
        { key: 'boundary', label: '경계' },   
        { key: 'safe', label: '안전' },    
    ];
});

// 비율 및 학생 수 계산
const processedChartData = computed(() => {

    if (!props.statistics || !Array.isArray(props.statistics)) {
        console.warn("Attainment: cannot process chart data.");
        return []; // 유효하지 않으면 빈 배열 반환
    }

    console.log('Attainment 차트 데이터 처리:', props.statistics);

    return achievementLevels.value.map(levelInfo => {
        const subcateName = levelInfo.subcate; 
        const stats = props.statistics.find(s => s.subcate === subcateName); // 해당 하위 영역 통계 찾기

        if (!stats) {
            console.warn(`통계 데이터를 찾을 수 없음: ${subcateName}`);
            return {
                label: subcateName,
                attention: { count: 0, percentage: 0 },
                boundary: { count: 0, percentage: 0 },
                safe: { count: 0, percentage: 0 },
            };
        }

        const total = stats.totalStudents || 0; // 총 학생 수 (0으로 나누는 것 방지)
        
        // 각 상태별 학생 수
        const attentionCount = stats.attentionCount || 0;
        const boundaryCount = stats.boundaryCount || 0;
        const safeCount = stats.safeCount || 0; 
        
        // 각 상태별 백분율 (소수점 첫째자리까지)
        const attentionPercentage = total > 0 ? Math.round((attentionCount / total) * 100 * 10) / 10 : 0;
        const boundaryPercentage = total > 0 ? Math.round((boundaryCount / total) * 100 * 10) / 10 : 0;
        const safePercentage = total > 0 ? Math.round((safeCount / total) * 100 * 10) / 10 : 0; 

        console.log(`${subcateName} 통계:`, {
            total,
            attention: { count: attentionCount, percentage: attentionPercentage },
            boundary: { count: boundaryCount, percentage: boundaryPercentage },
            safe: { count: safeCount, percentage: safePercentage }
        });

        return {
            label: subcateName, // 그래프 X축 라벨 
            attention: { count: attentionCount, percentage: attentionPercentage },
            boundary: { count: boundaryCount, percentage: boundaryPercentage },
            safe: { count: safeCount, percentage: safePercentage },
        };
    });
});

// 막대 높이(%) 계산
const getBarHeight = (percentage) => {
  const numericValue = Number(percentage);
  if (percentage === null || percentage === undefined || percentage === '' || isNaN(numericValue)) {
    return '0%';
  }
  
  // 100% 기준으로 막대 높이 계산
  const displayPercentage = numericValue; // 데이터 자체가 백분율이므로 그대로 사용
  // CSS 높이값 반환
  return Math.max(0, displayPercentage) + '%';
};

// 학생 수 또는 백분율 값을 포맷팅하여 반환
const getDisplayValue = (data, type) => { // data = { count, percentage } 객체
  if (!data || isNaN(Number(data.count)) || isNaN(Number(data.percentage))) {   
    return 'N%<br>(M명)';
  }

  // 요청된 타입에 따라 백분율 또는 학생 수 반환
  if (type === 'percentage') {
      return `${data.percentage.toFixed(1)}%`; // 소수점 첫째자리까지 표시
  }
  return `(${data.count}명)`;
};

// Y축에 표시될 라벨 목록
const yAxisGridPoints = computed(() => {
    const points = [];
    const maxDataValue = 100; // 고정값 100%
    const step = 10;

    // 데이터 값
    for (let dataValue = 0; dataValue <= maxDataValue; dataValue += step) {        
        const cssBottom = (dataValue / maxDataValue) * 100;
        points.push({
            dataLabel: dataValue, 
            cssBottom: cssBottom 
        });
    }
    // 데이터 스케일이 역순이므로 순서를 바꿔줌
    return points.reverse();
});
</script>

<template>
    <div class="card pb-5" :class="props.pagetype">
      <h5 class="card-title text-[20px]" :class="props.pagetype">{{ mainTitle }}</h5>
      <p class="pt-4 pb-2 font-medium">{{ subDescriptionTitle }}</p>
      <p class="text-[18px]" v-html="descriptionHtml"></p>      
    </div>
      
    <div class="chart-wrap" :class="props.pagetype"> 
      <div class="chart-y-axis" :style="{ height: chartHeight }">
          <div v-for="point in yAxisGridPoints" :key="point.dataLabel" class="y-axis-label"
          :style="{ bottom: point.cssBottom + '%' }">
          {{ point.dataLabel }}%
        </div>
      </div>
      <div class="chart-groups-wrap">         
        <div class="chart-grid-lines" :style="{ height: chartHeight }">
          <div v-for="point in yAxisGridPoints" :key="point.dataLabel" class="grid-line"
            :style="{ bottom: point.cssBottom + '%' }"></div>
        </div> 
        <div v-if="processedChartData.length" class="bar-chart-data">
            <div v-for="(item, itemIndex) in processedChartData" :key="itemIndex" class="bar-group">                
              <div class="bar-items-container" :style="{ height: chartHeight }">
                  <div v-for="(group, groupIndex) in fixedGroups" :key="groupIndex" class="bar-item">
                      <div
                          class="bar-segment"
                          :class="group.key"
                          :style="{ height: getBarHeight(item[group.key].percentage) }">                            
                          <div class="bar-label-value">                              
                                <p v-html="getDisplayValue(item[group.key], 'percentage')"></p>
                                <p v-html="getDisplayValue(item[group.key], 'count')"></p>                              
                          </div>
                      </div>
                  </div>
              </div>       
              <div class="legend-wrap">
                <div class="score-details">
                  <span v-for="(group, detailIndex) in fixedGroups" :key="detailIndex">
                      {{ group.label }}                        
                  </span>
                </div>
                <div class="group-label">
                    {{ item.label }}
                </div>  
              </div>   
            </div>
        </div> 
      </div>
    </div>

    <table :class="props.pagetype" class="mt-5">
      <thead>
        <tr>
          <th class="subcate">하위영역</th>
          <th class="status">{{ statusHeaderText }}</th>
          <th class="students">명단</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, rowIndex) in tableRows" :key="rowIndex">
          <td class="subcate" v-html="row.subcate"></td>
          <td class="status">
            <p v-for="(status, sIndex) in row.status" :key="sIndex" class="status-pill"
              :class="{ 'status-attention': status === '주의', 'status-boundary': status === '경계' }">{{ status }}</p>
          </td>
          <td class="students">           
            <p class="student-name">
              {{ filterStudentName(row.students[0]) }}
            </p>
            <p class="student-name">
              {{ filterStudentName(row.students[1]) }}
            </p>
          </td>
        </tr>
      </tbody>
    </table>
     <p class="pt-2 caution">'안전'에 해당하는 학생 명단은 생략함</p>
</template>

<style lang="scss" scoped>
table { 
  th {          
      text-align: center;
      font-weight: 400;    
      &:nth-child(1), &:nth-child(2) { 
          width: 15.525%;
        } 
  }
  td {     
      height: 100px;   
      text-align: center;
      &:nth-child(3) { 
          text-align: left;
      }
      p {
          border-bottom: 1px solid #ccc;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 14px;
          &:nth-child(2) {border: 0;}
      }
  }
  &.literacy {
    thead tr:nth-child(1) {
      background-color: var(--result-sky);
    }
  }
  &.numeracy {
    thead tr:nth-child(1) {
      background-color: var(--result-pink);
    }
    tbody tr:nth-child(4) td.subcate {padding: 0 20px;}
  }

  .students p {justify-content: flex-start;}
  .status p:nth-child(2), .students p:nth-child(2) {background-color: #FAFAFA;}
}    


/* === 막대 그래프 스타일 === */
.chart-wrap {
  position: relative;
  display: flex;  
  border: 1px solid #999;
  border-radius: 10px;
  padding: 70px 34px 30px 54px;

  &.literacy {
    --bar-color: rgba(var(--result-light-blue-rgb),.5);    
    --font-color: var( --result-blue);
    --border-color: var( --result-blue);
  }

  &.numeracy {
    --bar-color: rgba(var(--result-light-red-rgb),.5);   
    --font-color: var( --result-red);
    --border-color: var( --result-red); 
  }
}
.chart-y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  font-size: 14px;
  line-height: 0;
}
.chart-groups-wrap {
  width: 100%;
  position: relative;
  margin: 0 20px;
}
.chart-grid-lines {
  position: absolute;
  top: 1px;
  left: 0;
  width: 100%;
  height: var(--chart-height, 300px); 

  .grid-line {
    position: absolute;
    left: 0;
    width: 100%;
    border-top: 2px dashed #E9E9E9; 
    z-index: 0;
    transform: translateY(-1px);     
    &:last-child { 
        border-top: 2px solid #000; 
        z-index: 2; 
    }
  }
}
.bar-chart-data {
    display: flex;
    width: 100%;
    padding: 0 18px;    
}
.bar-group {
  width: calc(100% / 4);
  display: flex;
  flex-direction: column; 
  align-items: center;
  position: relative;
  &:nth-child(4) {
    .legend-wrap::after {
      content: '';
      position: absolute;
      width: 1px;
      height: 100%;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      background-image: url("@/assets/images/result/line_vertical.png");
      opacity: .9;
    }
  }
}
.bar-items-container {
    display: flex;
    align-items: flex-end; 
    justify-content: space-between; 
    width: 100%; 
    height: var(--chart-height, 300px); 
    position: relative; 
    gap: 40px;
    padding: 0 30px;
}
.bar-item {
    width: 20px; 
    position: relative; 
    display: flex;
    justify-content: center;
    align-items: flex-end; 
    height: 100%;     
}
.bar-segment {
  width: 100%; 
  transition: height 0.5s ease-out; 
  position: relative; 
  border-radius: 4px 4px 0 0; 
  background-color: var(--bar-color); 

  .bar-label-value {
    position: absolute;
    top: -60px;
    font-size: 18px;
    left: 50%;
    transform: translateX(-50%);
    text-align: center;
    font-weight: 500;
    line-height: 1.2;
  }
}
.legend-wrap {
  width: 100%;
  padding: 10px 14px;
  margin-top: 20px;
  font-weight: 500;
  position: relative;
  &::before {
      content: '';
      position: absolute;
      width: 1px;
      height: 100%;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      background-image: url("@/assets/images/result/line_vertical.png");
      opacity: .9;
    } 
  }
.score-details {
    position: relative;
    display: flex;
    width: 100%;
    justify-content: space-around;
    padding-bottom: 10px;
    
    span {
      display: block; 
      font-size: 18px;
      width: calc(100% / 3);
      text-align: center;
    }
}
.group-label {
    border-radius: 5px;
    border: 1px solid var(--border-color);
    color: var(--font-color);
    text-align: center;
    font-size: 18px;
    padding: 10px 0;
    line-height: 1;
}
.legend-item {
    display: flex;
    align-items: center;
    gap: 5px;
}
.legend-color-box {
    display: inline-block;
    width: 15px;
    height: 15px;
    border-radius: 3px;
    &.attention { background-color: var(--attention-bar-color); }
    &.boundary { background-color: var(--boundary-bar-color); }
    &.safe { background-color: var(--safe-bar-color); }
}
</style>